// In-app "What's New" release notes — the ProHR-internal counterpart to
// the Teams release-notes webhook, but summarized for end users with
// plain how-to instructions instead of engineering commit messages.
//
// Ordered NEWEST FIRST. Add a new entry to the top of RELEASES and bump
// CURRENT_VERSION whenever there's a user-facing change worth announcing.
// The WhatsNew pill compares CURRENT_VERSION against the last-seen
// version stored in localStorage to decide whether to show the unseen dot.

export const CURRENT_VERSION = '2026-10-21';

export const RELEASES = [
  {
    version: '2026-10-21',
    date: '2026-10-21',
    title: 'Birthdays on the team calendar',
    items: [
      {
        title: 'See your colleagues’ birthdays',
        detail: 'Birthdays now appear on the Team Calendar with a cake icon, every year. Only the day and month are shown — the year of birth is never shared. Use the Birthdays chip to show only birthdays.',
        howTo: 'Employee view → Team Calendar → look for the cake icon, or tap the Birthdays chip.',
      },
      {
        title: 'Admins can record birthdays',
        detail: 'A new Birthday field sits next to Hire date on each person’s details, and on the invite form. Leave it blank to keep someone off the calendar.',
        howTo: 'Admin → People → Edit a person → Birthday.',
      },
    ],
  },
  {
    version: '2026-09-01',
    date: '2026-09-01',
    title: 'Work-from-home and remote allowances',
    items: [
      {
        title: 'One work-from-home day a week',
        detail: 'WFH is now capped at one day per calendar week. The planner shows how many you have left, and the week resets every Monday.',
        howTo: 'Employee view → Planner → pick Work From Home. The remaining count sits with the request at checkout.',
      },
      {
        title: 'Remote work for managers and above',
        detail: 'Remote Work is now a proper leave type with five working days per calendar month, available to Manager level and above.',
        howTo: 'Employee view → Planner → pick Remote Work. If it says you are not eligible, ask your admin to set your level.',
      },
      {
        title: 'Admins set the limits',
        detail: 'Any leave type can be capped at a number of days per week or month, optionally restricted to a minimum employee level. Nothing accrues — unused days do not carry forward.',
        howTo: 'Admin → Leave Types → tick “Limit how often this can be taken”. Set people’s levels under Admin → People.',
      },
    ],
  },
  {
    version: '2026-08-11',
    date: '2026-08-11',
    title: 'Book one trip for several people',
    items: [
      {
        title: 'Request travel for colleagues',
        detail: 'Travelling with others? Add them to your travel request and it becomes one trip for the whole group instead of a separate request each.',
        howTo: 'Employee view → My Travel → New Request → tick the colleagues under “Who is travelling?”.',
      },
      {
        title: 'Everyone still tracks their own trip',
        detail: 'Each traveller sees the trip under My Travel and is emailed when they are added. The travel team sees one request covering everyone, and books or cancels it as a group.',
        howTo: 'Nothing to do — if a colleague adds you, the trip appears in My Travel automatically.',
      },
      {
        title: 'Add your own passport',
        detail: 'Whoever organises the trip usually will not have your passport scan, so you can upload it yourself against your trip.',
        howTo: 'My Travel → open the trip → “Upload your passport”.',
      },
    ],
  },
  {
    version: '2026-08-10',
    date: '2026-08-10',
    title: 'Approvers see one card per request cart',
    items: [
      {
        title: 'A cart is one approval, not ten',
        detail: 'When someone plans several days at once, the approval queue now shows a single card for the whole cart — “4 requests, Aug 13 → Aug 30” — with one Approve all / Reject all. Previously each day appeared as its own card and had to be actioned separately.',
        howTo: 'Approver view → Approval Queue. Grouped carts show a “N requests” tag.',
      },
      {
        title: 'You can still handle individual days',
        detail: 'If you agree with most of a cart but not all of it, expand it and approve or reject each day on its own. The employee still gets a single summary email listing what was approved and what wasn’t.',
        howTo: 'On a grouped card, click “Show N individual days”.',
      },
    ],
  },
  {
    version: '2026-07-27',
    date: '2026-07-27',
    title: 'One email per request cart — not one per day',
    items: [
      {
        title: 'Bulk requests get a single confirmation',
        detail: 'When you submit several days at once from the Planner (e.g. a run of Work-From-Home days), you now get ONE confirmation email for the whole cart instead of one per day.',
        howTo: 'Nothing to do — plan your days and check out as usual. The single email lists everything you submitted.',
      },
      {
        title: 'One decision email, with anything not approved called out',
        detail: 'Once your cart has been reviewed, you get a single outcome email: everything that was approved, plus a separate “Not approved” section listing any items that were declined and the reason.',
        howTo: 'Nothing to do — the summary arrives once the whole cart has been decided.',
      },
    ],
  },
  {
    version: '2026-07-16-2',
    date: '2026-07-16',
    title: 'Request Entry/Exit visas in ProHR',
    items: [
      {
        title: 'Request a visa',
        detail: 'You can now request Exit/Re-entry, Final Exit, or Entry visas right in ProHR. Requests go through the normal approval chain, just like leave and expenses.',
        howTo: 'Employee view → My Visas → “Request a visa”. Track its status there.',
      },
      {
        title: 'HR issues it + you get renewal reminders',
        detail: 'Once approved, HR uploads the issued visa and enters its validity dates. As the expiry approaches, you’re reminded automatically to submit a renewal — so it never lapses.',
        howTo: 'Nothing to do — the reminder emails you before the visa expires, with a link to request a renewal.',
      },
    ],
  },
  {
    version: '2026-07-16',
    date: '2026-07-16',
    title: 'Upload your own documents + set expiry reminders',
    items: [
      {
        title: 'Add your own documents',
        detail: 'You can now upload your own documents (passport, ID, certificates…) under My Documents — not just the ones your admin adds.',
        howTo: 'Employee view → My Documents → “Add document”. Attach the file and give it a name.',
      },
      {
        title: 'Get reminded before they expire',
        detail: 'Set an expiry date and choose how many days before you want a heads-up. We’ll email you (and show an in-app alert) so nothing lapses unnoticed.',
        howTo: 'When adding a document, set the expiry date and “remind me N days before”. Edit or delete your own documents anytime.',
      },
    ],
  },
  {
    version: '2026-07-15',
    date: '2026-07-15',
    title: 'One daily approvals email instead of many',
    items: [
      {
        title: 'Approvers get a single 9am digest — in their own timezone',
        detail: 'Approvers are no longer emailed once per submission. Instead, each approver receives one email at 9am their local time, listing every leave and expense request still waiting on them — so a busy day is one email, not ten. Your timezone is detected automatically.',
        howTo: 'Nothing to do. Prefer instant per-request emails instead? Admin → Settings → Approval Reminders → “Also email approvers instantly on each submission”.',
      },
      {
        title: 'Submitters still get instant confirmation',
        detail: 'When you submit a leave or expense request, you immediately get a confirmation that it was received and is awaiting approval — even though the approver now hears about it in their daily digest.',
        howTo: 'Nothing to do — the confirmation is automatic.',
      },
    ],
  },
  {
    version: '2026-07-13',
    date: '2026-07-13',
    title: 'Finance can correct expense line items',
    items: [
      {
        title: 'Edit amounts on any claim (Finance & Admin)',
        detail: 'Finance and admins can now correct the individual line items on an expense claim — amount, currency, exchange rate, date and description — and the SAR total plus the 3% foreign-currency fee recalculate automatically. Paid claims are locked until their status is changed.',
        howTo: 'Admin → Expense Requests → open a claim → “Edit line items”. Fix the rows, then “Save line items”.',
      },
    ],
  },
  {
    version: '2026-07-12',
    date: '2026-07-12',
    title: 'Foreign-currency fee now follows your bank location',
    items: [
      {
        title: '3% fee based on your bank location',
        detail: 'The 3% foreign-transaction fee now applies to any expense in a currency other than your bank’s home currency — not only non-SAR. Your admin sets each person’s Bank Location under People.',
        howTo: 'Nothing to do. If a foreign expense’s SAR total looks off, ask your admin to check your Bank Location.',
      },
      {
        title: 'Admins: recompute fees on in-flight claims',
        detail: 'Setting an employee’s Bank Location now auto-updates their draft and pending claims, and there’s a bulk action to re-apply the rule to existing ones.',
        howTo: 'Admin → Expense Requests → “Recompute FX fees (pending & drafts)”.',
      },
    ],
  },
  {
    version: '2026-07-08',
    date: '2026-07-08',
    title: 'Expense module upgrades',
    items: [
      {
        title: 'Automatic 3% foreign-currency fee',
        detail: 'Non-SAR expenses now automatically include the day’s exchange rate plus a 3% foreign transaction fee. The SAR total shows on each line.',
        howTo: 'Just enter the amount and currency — the SAR total (rate + 3% fee) is calculated for you.',
      },
      {
        title: 'Your work autosaves',
        detail: 'Expense drafts now save automatically as you go, and you’ll be warned before leaving a page with unsaved changes.',
        howTo: 'Nothing needed — just keep working. Reopen a draft any time from My Expenses.',
      },
      {
        title: 'Edit & resubmit rejected claims',
        detail: 'A rejected claim can now be edited and resubmitted without losing its reference number.',
        howTo: 'Open the rejected claim → Edit → make your changes → Resubmit.',
      },
      {
        title: 'Up to 10 receipts per claim',
        detail: 'Each expense claim now allows up to 10 receipts.',
        howTo: 'Need more than 10? Start a separate claim for the extra receipts.',
      },
      {
        title: 'Approval History for approvers',
        detail: 'Approvers can now see a history of the claims they’ve approved.',
        howTo: 'Go to Approvals → Approval History.',
      },
      {
        title: 'Monthly report + Paid status (Finance)',
        detail: 'Finance now has a monthly summary report and a Mark-as-paid action for approved claims.',
        howTo: 'Admin → Monthly Report for the summary. Open an approved claim → Mark as paid.',
      },
      {
        title: 'See who approved and when',
        detail: 'Approval dates now show alongside each approver on a claim’s approval chain.',
        howTo: 'Open any claim and check its approval chain — dates now appear next to each approver.',
      },
    ],
  },
];

export default RELEASES;
